/**
 * docCss.js
 *
 * Prototype-matching CSS string for all document preview and print/download use.
 * Inject as <style> in standalone HTML or as a <style> tag prepended to preview HTML.
 *
 * Mirrors the DOC_CSS constant from complyv_pro_final.html exactly.
 */

export const DOC_CSS = `
body{font-family:"Times New Roman",Times,serif;font-size:13pt;line-height:1.8;margin:0;padding:0;color:#000}
.doc-preview{padding:20px 24px;font-size:13pt;line-height:1.8}
.doc-letterhead{text-align:center;border-bottom:3px double #000;padding-bottom:18px;margin-bottom:22px}
.doc-company-name{font-size:16pt;font-weight:bold;text-transform:uppercase;letter-spacing:2px;margin-bottom:5px}
.doc-company-sub{font-size:10pt;color:#333;margin-bottom:4px}
.doc-company-cin{font-size:10pt;font-style:italic;color:#555;margin-bottom:4px}
.doc-doc-title{font-size:14pt;font-weight:bold;text-align:center;text-decoration:underline;margin:18px 0 14px;text-transform:uppercase;letter-spacing:1px}
.doc-separator{border:none;border-top:1.5px solid #000;margin:14px 0}
.doc-separator-double{border:none;border-top:3px double #000;margin:14px 0}
.doc-section-head{font-size:11pt;font-weight:bold;text-decoration:underline;margin:16px 0 8px;text-transform:uppercase;break-after:avoid;page-break-after:avoid}
.doc-para{font-size:12pt;text-align:justify;margin-bottom:10px;line-height:1.8;orphans:3;widows:3}
.doc-resolved{font-size:12pt;font-style:italic;margin:10px 0 10px 24px;text-align:justify;padding-left:12px;border-left:3px solid #333;line-height:1.8;break-inside:avoid;page-break-inside:avoid}
.doc-sign-block{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:50px;break-inside:avoid;page-break-inside:avoid}
.doc-sign-line{border-top:1.5px solid #000;padding-top:6px;margin-top:40px}
.doc-sign-name{font-weight:bold;font-size:12pt}
.doc-sign-desig{font-size:11pt}
.doc-sign-din{font-size:10.5pt;color:#444}
.doc-footer{text-align:center;font-size:9pt;color:#777;border-top:1px solid #ccc;padding-top:8px;margin-top:40px}
.doc-watermark{text-align:right;font-size:9pt;color:#ccc;font-style:italic}
.doc-meeting-table{width:100%;border-collapse:collapse;margin-bottom:16px}
.doc-meeting-table td{padding:6px 8px;font-size:12pt;vertical-align:top}
.doc-meeting-table td:first-child{font-weight:bold;width:180px;white-space:nowrap}
/* table-layout:fixed — the overflow armour's overflow-wrap:anywhere collapses
   auto-layout min-content to ~1ch, which let the browser starve the NAME
   column (per-letter breaks in the canvas). Fixed layout splits the width
   evenly regardless, so names wrap at spaces; the slim first column keeps
   S.No from wasting an equal share. */
.doc-attendance-table{width:100%;border-collapse:collapse;margin:12px 0;break-inside:avoid;page-break-inside:avoid;table-layout:fixed}
.doc-attendance-table th:first-child{width:46px}
.doc-attendance-table th,.doc-attendance-table td{border:1px solid #000;padding:7px 10px;font-size:11pt;text-align:left}
.doc-attendance-table th{background:#f0f0f0;font-weight:bold;font-size:10pt}
.doc-agenda-summary{break-inside:avoid;page-break-inside:avoid}
.doc-agenda-item-title{break-after:avoid;page-break-after:avoid}

/* ── Generic tables (AI-generated markdown → <table> without a doc- class).
     Without this, GFM tables from the drafting pipeline rendered as unstyled,
     borderless grids — the "formatting sucks" complaint. ─────────────────── */
.doc-preview table:not([class]){width:100%;border-collapse:collapse;margin:12px 0;break-inside:avoid;page-break-inside:avoid}
.doc-preview table:not([class]) th,.doc-preview table:not([class]) td{border:1px solid #444;padding:7px 10px;font-size:12pt;text-align:left;vertical-align:top}
.doc-preview table:not([class]) th{background:#f0f0f0;font-weight:bold;text-align:center;white-space:nowrap}
/* 4+ column generic tables (directors-present, pasted grids): auto layout +
   the armour's overflow-wrap:anywhere collapses min-content to ~1ch, so a
   nowrap header ("MODE OF PARTICIPATION") could starve NAME down to per-letter
   breaks. Fixed layout splits the width evenly and headers wrap. 2–3 column
   tables (Day & Date : value) keep auto layout — equal split would misshape
   their label/colon columns. */
.doc-preview table:not([class]):has(tr>:nth-child(4)){table-layout:fixed}
.doc-preview table:not([class]):has(tr>:nth-child(4)) th{white-space:normal}

/* ── Auditor's Report / statutory report classes ─────────────────────────── */
.doc-addressee{font-weight:bold;font-size:12pt;line-height:1.7;margin:16px 0 12px}
.doc-rep-section{font-size:13pt;font-weight:bold;text-decoration:underline;margin:20px 0 8px;break-after:avoid;page-break-after:avoid}
.doc-rep-head{font-size:12pt;font-weight:bold;margin:15px 0 6px;break-after:avoid;page-break-after:avoid}
.doc-ph{background:#fdf3d8;color:#7a5a00;font-weight:600;padding:0 3px;border-radius:2px}
.doc-alt-note{font-style:italic;color:#555}
.doc-center{text-align:center}
.doc-clause{font-size:12pt;text-align:justify;line-height:1.8;margin:7px 0;padding-left:24px;text-indent:-24px}
.doc-subclause{font-size:12pt;text-align:justify;line-height:1.8;margin:6px 0 6px 24px;padding-left:30px;text-indent:-30px}
.doc-list{margin:8px 0 12px 26px;padding:0}
.doc-list li{font-size:12pt;text-align:justify;line-height:1.8;margin-bottom:7px}
.doc-kam-table{width:100%;border-collapse:collapse;margin:10px 0 14px;break-inside:avoid;page-break-inside:avoid}
.doc-kam-table th,.doc-kam-table td{border:1px solid #555;padding:8px 10px;font-size:11pt;text-align:left;vertical-align:top}
.doc-kam-table th{background:#f0f0f0;font-weight:bold;text-align:center}
.doc-notice-card{border:1px solid #999;background:#f8f8f8;padding:14px 18px;margin:16px 0;font-size:11.5pt}

/* ── A4 overflow armour: unbroken runs (CINs, pasted URLs, reference numbers)
     must WRAP, never widen the layout past the printable width — auto-layout
     tables size to min-content, and overflow-wrap:anywhere makes that
     breakable, so a 90-char token can't blow the page out to 2000px and clip
     off the right edge of the PDF. Verified by scripts/test_minutes_pdf_a4.js. */
.doc-preview{overflow-wrap:anywhere}
.doc-preview table{max-width:100%}
@media print{body{margin:0;padding:0}@page{margin:1.5cm}.doc-preview{padding:0;max-width:none}}
`.trim();

/**
 * In-app injection variant of DOC_CSS — use this for any `<style>` rendered
 * into the LIVE app DOM (a sibling of a preview), NOT DOC_CSS.
 *
 * A `<style>` element is document-global: it is NOT scoped to where it sits in
 * the React tree. DOC_CSS opens with a `body{font-family:Times;font-size:13pt;
 * line-height:1.8;…}` rule (and a print-time `body` reset) meant for a STANDALONE
 * document, whose root element IS the page <body>. Injected into the running app,
 * that rule restyles the ENTIRE shell — the fixed nav sidebar, the header and
 * every page inherit Times / 13pt / line-height 1.8. On the sidebar this inflates
 * every nav row (each icon span's line box jumps to 1.8×), so the sidebar appears
 * to "stretch vertically" whenever a document page or draft modal is open.
 *
 * Every other DOC_CSS rule is already scoped to a `.doc-*` class, and each in-app
 * document wrapper sets its own Times/13pt/1.8 inline (and `.doc-preview` carries
 * the same), so dropping the `body`-level rules is a no-op for the document while
 * removing the global leak.
 */
export const DOC_CSS_SCOPED = DOC_CSS.replace(/body\{[^}]*\}/g, '').trim();
